import React, { useMemo, useState } from "react";
import { Button, Table, Tooltip, message, Progress } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { insertCRFDataIntoWordAtPage } from "../../CRF";

export const StyledTableWrapper = styled.div`
  .ant-table-thead > tr > th {
    background: #426bba !important;
    border: 1px solid #426bba;
    color: #fefeff !important;
    font-weight: 600;
    font-size: 12px;
    border: 1px solid #426bba;
    line-height: 1.2;
  }

  .ant-table-tbody > tr > td {
    background: linear-gradient(
      150deg,
      rgba(235, 240, 255, 1) 25%,
      rgba(225, 235, 255, 1) 50%,
      rgba(215, 230, 255, 1) 100%
    );
    border: 1px solid #bfd3f2;
    color: #2c4c91;
    font-size: 11px;
    padding: 6px 12px;
    line-height: 1;
  }

  /* Parent (form) rows: a touch bolder so the group header reads clearly */
  .ant-table-tbody > tr.crf-group-row > td {
    background: linear-gradient(
      150deg,
      rgba(225, 233, 255, 1) 25%,
      rgba(213, 226, 255, 1) 50%,
      rgba(201, 220, 255, 1) 100%
    );
    font-weight: 600;
  }

  /* Folder (child) rows: slightly lighter to sit visually "under" the form */
  .ant-table-tbody > tr.crf-child-row > td {
    background: rgba(244, 248, 255, 1);
  }
`;

const StyledButton = styled(Button)`
  background: #e3ecff;
  color: #426bba !important;
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 1rem;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  padding: 4px 16px;

  &:hover {
    background: #d7e4ff !important;
    color: #426bba !important;
  }
`;

const TemplateTable = ({ columns, dataSource, templateProgress }) => {
  // Progress state for the "Insert All" action.
  const [insertingAll, setInsertingAll] = useState(false);
  const [insertProgress, setInsertProgress] = useState(0);

  const handleInsertAllToWord = async (dataSource) => {
    setInsertingAll(true);
    setInsertProgress(0);
    try {
      for (let i = 0; i < dataSource.length; i++) {
        await insertCRFDataIntoWordAtPage(i, dataSource[i]);
        setInsertProgress(Math.round(((i + 1) / dataSource.length) * 100));
      }
      message.success("All CRF forms inserted into Word successfully!");
    } catch (err) {
      console.error("Error inserting all data:", err);
      message.error("Failed to insert all data into Word");
    } finally {
      setInsertingAll(false);
    }
  };

  // Group the flat dataSource by form so one form shows as a single
  // collapsible parent row whose children are its folders. Each child keeps
  // its original flat-array index (__index) so the eye / write-to-Word actions
  // still target the exact same Word page as the un-grouped table did.
  const groupedData = useMemo(() => {
    const map = new Map();
    dataSource.forEach((item, idx) => {
      const formName = item?.form ?? "—";
      if (!map.has(formName)) {
        map.set(formName, {
          key: `form::${formName}`,
          form: formName,
          isGroup: true,
          children: [],
        });
      }
      map.get(formName).children.push({
        ...item,
        __index: idx,
        isGroup: false,
        key: `row::${idx}`,
      });
    });
    return Array.from(map.values());
  }, [dataSource]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "1rem",
          margin: "1rem 0",
        }}
      >
        {insertingAll && (
          <div style={{ flex: 1, maxWidth: 240 }}>
            <Progress
              percent={insertProgress}
              size="small"
              status={insertProgress === 100 ? "success" : "active"}
              strokeColor="#426bba"
            />
          </div>
        )}
        <StyledButton
          type="primary"
          onClick={() => handleInsertAllToWord(dataSource)}
          disabled={templateProgress < 100 || insertingAll}
          loading={insertingAll}
        >
          Insert All
        </StyledButton>
      </div>
      <StyledTableWrapper>
        <Table
          dataSource={groupedData}
          columns={columns}
          pagination={false}
          rowKey="key"
          indentSize={0}
          rowClassName={(record) =>
            record.isGroup ? "crf-group-row" : "crf-child-row"
          }
          expandable={{
            // A single down/right chevron on each form row; folders appear on expand.
            expandIcon: ({ expanded, onExpand, record }) =>
              record.isGroup ? (
                <span
                  onClick={(e) => onExpand(record, e)}
                  style={{
                    cursor: "pointer",
                    marginRight: 8,
                    color: "#426bba",
                  }}
                >
                  {expanded ? <DownOutlined /> : <RightOutlined />}
                </span>
              ) : null,
          }}
        />
      </StyledTableWrapper>
    </>
  );
};

export default TemplateTable;
